import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../api/axios';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState(''); // persistent inline error
  const { login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInlineError(''); // clear previous error only when user retries
    setLoading(true);
    try {
      const res = await api.post('/api/auth/login', { email, password });
      login(res.data);
      toast.success(`Welcome back, ${res.data.name}!`);
      const role = res.data.role;
      if (role === 'KAM') navigate('/kam/home');
      else if (role === 'SUPPORT') navigate('/support/dashboard');
      else navigate('/admin/dash/home');
    } catch (err) {
      const msg = err.response?.data?.error || 'Invalid email or password. Please try again.';
      setInlineError(msg); // stays on screen permanently until next attempt
      toast.error(msg);    // also fire toast (belt + suspenders)
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold">O</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Omnicard Enterprise Management System</h1>
          <p className="text-gray-500 text-sm mt-1">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Inline error — always visible, never auto-clears */}
          {inlineError && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-300 text-red-800 rounded-xl px-4 py-3">
              <span className="flex-shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center text-red-600 text-xs font-bold mt-0.5">✕</span>
              <p className="text-sm font-medium flex-1">{inlineError}</p>
              <button
                type="button"
                onClick={() => setInlineError('')}
                className="flex-shrink-0 text-red-400 hover:text-red-700 text-lg leading-none"
              >
                ×
              </button>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              placeholder="Enter your email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              placeholder="Enter your password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-primary-light text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-xs text-gray-300 mt-4">v1.7.0 · 2026-05-19</p>
      </div>
    </div>
  );
};

export default LoginPage;
